import { cast, getSnapshot, Instance, types } from 'mobx-state-tree';

import AccountPayable, { IAccountPayable } from './AccountPayable';
import { AccountsPayableLinkStatus } from '@constants/accountsPayableLink';

export interface TxnFirebaseUpdate {
  status: string;
  batchStatus: string;
  error: string | null;
  cepUrl: string | null;
  totalDispersed: number;
}

const TXN_SUCCESS_STATUSES = [
  'DISPERSION_SUCCESS',
  'INVALID_CURRENCY',
  'INVALID_BANK_ACCOUNT',
  'INVALID_CURRENCY_AND_BANK_ACCOUNT',
  'CANCELED',
];

const TXN_FAIL_STATUSES = ['DISPERSION_FAIL', 'REFUNDED', 'RETURNED'];

const AccountsPayable = types
  .model({
    canProcess: types.optional(types.array(AccountPayable), []),
    canNotProcess: types.optional(types.array(AccountPayable), []),
    inProcess: types.optional(types.array(AccountPayable), []),
    accountsPayableIds: types.optional(types.array(types.string), []),
    selectedIds: types.optional(types.array(types.string), []),
    unselectedIds: types.optional(types.array(types.string), []),
    totalAmount: types.optional(types.number, 0),
  })
  .views((self) => ({
    get getCanProcessLength() {
      return self.canProcess.length;
    },
    get getCanNotProcessLength() {
      return self.canNotProcess.length;
    },
    get getSelectedIds() {
      return self.selectedIds;
    },
    get getUnselectedIds() {
      return self.unselectedIds;
    },
    get getSelectedIdsLength() {
      return self.selectedIds.length;
    },
    get getUnselectedIdsLength() {
      return self.unselectedIds.length;
    },
    get getTotalAmount() {
      return self.totalAmount;
    },
  }))
  .actions((self) => ({
    setSelectedIds: (ids: any) => {
      self.selectedIds = ids;
    },
    setUnselectedIds: (ids: any) => {
      self.unselectedIds = ids;
    },
    handleGlobalTotalAmount: () => {
      const unselectedsIds = self.getUnselectedIds;
      const accountsPayable = [...self.canProcess, ...self.canNotProcess]
        .map((item: any) => !item.error && item)
        .filter(Boolean) as string[];

      let totalAmount = 0;
      for (const ap of accountsPayable) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const amount = ap.amount as any;
        const itemAmount = Number(amount);
        totalAmount = totalAmount + itemAmount;
      }

      let totalUnselectedAmount = 0;
      for (const id of unselectedsIds) {
        const ap = accountsPayable
          .map((item: any) => item.accountPayableId === id && Number(item.amount))
          .filter(Boolean) as number[];
        if (Object.keys(ap).length > 0) {
          totalUnselectedAmount = totalUnselectedAmount + ap[0];
        }
      }
      self.totalAmount = cast(totalAmount - totalUnselectedAmount);
    },
    updateAccountsPayable: (data: any, status: string) => {
      const cP = data?.canProcess || [];
      const cNP = data?.canNotProcess || [];
      const inP = data?.inProcess || [];

      const canProcess = [];
      const canNotProcess = [];
      const inProcess = [];

      if (cP.length > 0) {
        for (const item of cP) {
          const netsuite = item?.netsuite || {};
          if (item?.netsuite) {
            delete item.netsuite;
          }
          canProcess.push({ ...item, ...netsuite });
        }
      }
      if (cNP.length > 0) {
        for (const item of cNP) {
          const netsuite = item?.netsuite || {};
          if (item?.netsuite) {
            delete item.netsuite;
          }
          canNotProcess.push({ ...item, ...netsuite });
        }
      }
      if (inP.length > 0) {
        for (const item of inP) {
          const netsuite = item?.netsuite || {};
          if (item?.netsuite) {
            delete item.netsuite;
          }
          inProcess.push({ ...item, ...netsuite });
        }
      }
      const rows = [...canProcess, ...canNotProcess, ...inProcess];

      let accountsPayableIds = rows
        .map((item) => !item.error && item.accountPayableId)
        .filter(Boolean) as string[];

      const beforeDispersionStatus = [
        AccountsPayableLinkStatus.ACTIVE,
        AccountsPayableLinkStatus.CONFIRMED,
        AccountsPayableLinkStatus.CANCELED,
        AccountsPayableLinkStatus.FUNDED,
      ];

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (!beforeDispersionStatus.includes(status)) {
        accountsPayableIds = rows.map((item) => item.accountPayableId);
      }

      self.accountsPayableIds = cast(accountsPayableIds);
      self.selectedIds = cast(accountsPayableIds);

      self.canProcess = cast(
        canProcess.map((item: IAccountPayable) => ({
          ...item,
        })),
      );
      self.canNotProcess = cast(
        canNotProcess?.map((item: IAccountPayable) => ({
          ...item,
        })),
      );
      self.inProcess = cast(
        inProcess?.map((item: IAccountPayable) => ({
          ...item,
        })),
      );
    },
    applyFirebaseTxnUpdates: (updates: Record<string, TxnFirebaseUpdate>) => {
      for (const [accountPayableId, update] of Object.entries(updates)) {
        const inProcessIdx = self.inProcess.findIndex(
          (i) => i.accountPayableId === accountPayableId,
        );
        const canProcessIdx = self.canProcess.findIndex(
          (i) => i.accountPayableId === accountPayableId,
        );
        const canNotProcessIdx = self.canNotProcess.findIndex(
          (i) => i.accountPayableId === accountPayableId,
        );

        const isSuccess = TXN_SUCCESS_STATUSES.includes(update.status);
        const isFail = TXN_FAIL_STATUSES.includes(update.status);

        if (inProcessIdx !== -1) {
          const snapshot = {
            ...getSnapshot(self.inProcess[inProcessIdx]),
            status: update.status,
            error: update.error ?? '',
            cepUrl: update.cepUrl ?? '',
          };
          self.inProcess.splice(inProcessIdx, 1);
          if (isSuccess) {
            self.canProcess.push(cast(snapshot));
          } else if (isFail) {
            self.canNotProcess.push(cast(snapshot));
          }
        } else if (canProcessIdx !== -1 && isFail) {
          const snapshot = {
            ...getSnapshot(self.canProcess[canProcessIdx]),
            status: update.status,
            error: update.error ?? '',
            cepUrl: update.cepUrl ?? '',
          };
          self.canProcess.splice(canProcessIdx, 1);
          self.canNotProcess.push(cast(snapshot));
        } else if (canNotProcessIdx !== -1 && isSuccess) {
          const snapshot = {
            ...getSnapshot(self.canNotProcess[canNotProcessIdx]),
            status: update.status,
            error: update.error ?? '',
            cepUrl: update.cepUrl ?? '',
          };
          self.canNotProcess.splice(canNotProcessIdx, 1);
          self.canProcess.push(cast(snapshot));
        } else if (canProcessIdx !== -1) {
          self.canProcess[canProcessIdx].cepUrl = update.cepUrl ?? '';
          self.canProcess[canProcessIdx].error = update.error ?? '';
        } else if (canNotProcessIdx !== -1) {
          self.canNotProcess[canNotProcessIdx].cepUrl = update.cepUrl ?? '';
          self.canNotProcess[canNotProcessIdx].error = update.error ?? '';
        }
      }
    },
  }));

export interface AccountsPayableStore extends Instance<typeof AccountsPayable> {}
export default AccountsPayable;
